import { useEffect, useState } from 'react'
import {
  collection, query, where, orderBy, limit, onSnapshot,
  type QueryConstraint,
} from 'firebase/firestore'
import { db } from '@fleetops/firebase'
import { firestorePaths, type Damage, type DamageStatus } from '@fleetops/types'
import { useSession } from '@/store/auth.store'
import { fleetQueryChunks, fleetScopeKey } from '@/lib/fleetScope'
import { subscriptionError, warnIfTruncated } from '@/lib/firestoreError'

// Ceiling on a single listener. This query used to be unbounded: every page
// that mounts this hook read the tenant's entire damages history, live, and
// re-read it on every navigation. The archive gives closed work a reason to
// accumulate for years, so the cost grew with the age of the project rather
// than with how much work was actually open.
//
// `createdAt desc` is already the order, so the cap keeps the newest — which
// is what an operational page wants. Pages that genuinely need history raise
// it. A per-page date window would cut deeper still, but not while the
// dashboard's cycle-time KPI derives its scope from this same list: see the
// note in useDashboardData.
const DEFAULT_MAX = 1000

interface UseDamagesOptions {
  fleetId?: string
  trailerId?: string
  assignedToId?: string
  status?: DamageStatus
  /** Ceiling on documents loaded per listener. Defaults to DEFAULT_MAX. */
  max?: number
}

export function useDamages(options: UseDamagesOptions = {}) {
  const session = useSession()
  const [damages, setDamages] = useState<Damage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const scopeKey = fleetScopeKey(session)
  const max = options.max ?? DEFAULT_MAX

  useEffect(() => {
    if (!session) return

    // An explicit fleetId narrows to one fleet and replaces the scope filter;
    // otherwise one listener per `in`-sized chunk of the caller's fleets. The
    // old code sliced the scope at ten and dropped the rest with no error, so
    // a user assigned to more than ten fleets lost damages silently — and the
    // deep link into one of them did nothing, because the case was never in
    // the list to be found.
    const chunks = options.fleetId ? [null] : fleetQueryChunks(session)
    const perChunk = new Map<number, Damage[]>()

    // Reported as well as stored. Most callers of this hook take `damages` and
    // `isLoading` and drop `error` on the floor — the Dashboard, the Centro de
    // Comando, o Arquivo, a Pesquisa — so a failed listener rendered an empty
    // page with no message anywhere. Storing it keeps the callers that do show
    // an error working; reporting it covers the ones that don't.
    const report = subscriptionError('as avarias', () => setIsLoading(false))

    const unsubscribes = chunks.map((chunk, i) => {
      const constraints: QueryConstraint[] = [
        where('tenantId', '==', session.tenantId),
        ...(options.fleetId
          ? [where('fleetId', '==', options.fleetId)]
          : chunk
            ? [where('fleetId', 'in', chunk)]
            : []),
        ...(options.trailerId ? [where('trailerId', '==', options.trailerId)] : []),
        ...(options.assignedToId ? [where('assignedToId', '==', options.assignedToId)] : []),
        ...(options.status ? [where('status', '==', options.status)] : []),
        orderBy('createdAt', 'desc'),
        limit(max),
      ]

      return onSnapshot(
        query(collection(db, firestorePaths.damages(session.tenantId)), ...constraints),
        (snap) => {
          warnIfTruncated('as avarias', snap.size, max)
          perChunk.set(i, snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Damage))
          // Each chunk arrives sorted; their union is not.
          const byId = new Map<string, Damage>()
          for (const list of perChunk.values()) for (const d of list) byId.set(d.id, d)
          setDamages([...byId.values()].sort(
            (a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0),
          ))
          setIsLoading(false)
        },
        (err) => {
          setError(err)
          report(err)
        },
      )
    })

    return () => unsubscribes.forEach((u) => u())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.tenantId, session?.role, scopeKey, options.fleetId, options.trailerId,
      options.assignedToId, options.status, max])

  return { damages, isLoading, error }
}
